/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, InputNumber, Row, Col, Typography, notification, Button, Select } from 'antd';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../constants/ndtPipelineTypeOptions';
import ClientLikeSelect from './ClientLikeSelect';

const { Title, Text } = Typography;
const { Option } = Select;

// Planned work options array (sorted alphabetically)
const plannedWorkOptions = [
  { value: 'Components', label: 'Components' },

  { value: 'Days', label: 'Days'},
  { value: 'Field Joints', label: 'Field Joints' },
  { value: 'Welds', label: 'Welds' },
];

/** Build production-line rows from API SOW record (welding/coating station lists + ndtStations). */
function productionLineRowsFromSow(record, projectType) {
  if (!record) return [];
  const ndtRaw = record.ndtStations || record.ndt_stations || [];
  const ndtByNum = {};
  ndtRaw.forEach((entry) => {
    const n = entry.stationNumber ?? entry.station_number;
    const t = entry.ndtType ?? entry.ndt_type;
    if (n != null && n !== '') ndtByNum[Number(n)] = t || '';
  });

  const weld = record.weldingStations || record.welding_stations;
  const coat = record.coatingStations || record.coating_stations;
  let nums = [];
  if (projectType === 'Welding') {
    nums = Array.isArray(weld) ? [...weld] : [];
  } else if (projectType === 'Coating') {
    nums = Array.isArray(coat) ? [...coat] : [];
  } else {
    nums = [
      ...new Set([
        ...(Array.isArray(weld) ? weld : []),
        ...(Array.isArray(coat) ? coat : []),
      ]),
    ];
  }
  nums = nums
    .map((x) => Number(x))
    .filter((n) => !Number.isNaN(n) && n > 0);

  // Union with every station listed in ndtStations (API can list NDT-only stations not in coating/welding arrays)
  const numsFromNdt = ndtRaw
    .map((e) => Number(e.stationNumber ?? e.station_number))
    .filter((n) => !Number.isNaN(n) && n > 0);
  nums = [...new Set([...nums, ...numsFromNdt])].sort((a, b) => a - b);

  const rows = nums.map((n, idx) => ({
    id: `pl-${record.id}-${n}-${idx}`,
    stationNumber: String(n),
    ndtType: ndtByNum[n] || '',
  }));
  // One extra blank row at the end so users can add another station without clicking Add row
  rows.push({
    id: `pl-${record.id}-blank-${Date.now()}`,
    stationNumber: '',
    ndtType: '',
  });
  return rows;
}

/** When production-line UI is hidden (coating), preserve API payload on pipeline edit. */
function copyProductionLineFromSowRecord(record, projectType) {
  if (!record || typeof record !== 'object') return {};
  const out = {};
  const sortedUniqueInts = (arr) =>
    [
      ...new Set(
        (Array.isArray(arr) ? arr : [])
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n) && n > 0)
      ),
    ].sort((a, b) => a - b);

  if (projectType === 'Welding') {
    const w = sortedUniqueInts(record.weldingStations || record.welding_stations);
    if (w.length) out.weldingStations = w;
  } else if (projectType === 'Coating') {
    const c = sortedUniqueInts(record.coatingStations || record.coating_stations);
    if (c.length) out.coatingStations = c;
  } else if (projectType === 'Both') {
    const w = sortedUniqueInts(record.weldingStations || record.welding_stations);
    const c = sortedUniqueInts(record.coatingStations || record.coating_stations);
    if (w.length) out.weldingStations = w;
    if (c.length) out.coatingStations = c;
  }

  const ndtRaw = record.ndtStations || record.ndt_stations;
  if (Array.isArray(ndtRaw) && ndtRaw.length > 0) {
    const ndtStations = ndtRaw
      .map((entry) => {
        const stationNumber = Number(
          typeof entry === 'object' && entry != null
            ? entry.stationNumber ?? entry.station_number
            : entry
        );
        const ndtType =
          typeof entry === 'object' && entry != null ? entry.ndtType ?? entry.ndt_type ?? '' : '';
        return { stationNumber, ndtType };
      })
      .filter((x) => !Number.isNaN(x.stationNumber) && x.stationNumber > 0);
    if (ndtStations.length) out.ndtStations = ndtStations;
  }
  return out;
}

const AddNewPipeline = (props) => {
  const {
    isModalVisible,
    setIsModalVisible,
    editDetailsForm,
    onSave,
    editValue,
    setEditValue,
    keyValue,
    setKeyValue,
    selectedProject,
    setChecked,
    checked,
    restrict,
    setRestrict,
    pipelineData,
    uniquePipeId,
    isWriteAccess,
    editingSowRecord,
    setEditingSowRecord,
  } = props;

  const [pipelineSec, setPipelineSec] = useState('');
  const [plannedWelds, setPlannedWelds] = useState('');
  // Coating-related fields
  const [material, setMaterial] = useState('');
  const [stalkId, setStalkId] = useState('');
  const [fieldJointType, setFieldJointType] = useState('');
  const [blastStandard, setBlastStandard] = useState('');
  const [pipeStringId, setPipeStringId] = useState('');
  // Production Line Set-Up — row: { id, stationNumber: '', ndtType: '' }
  const [plStationRows, setPlStationRows] = useState([]);
  const [pipelineSubmitting, setPipelineSubmitting] = useState(false);
  const submitLockRef = useRef(false);

  // Show coating fields only for Coating or Both project types
  const projectType = selectedProject?.projectType ?? '';
  const showCoatingFields = projectType === 'Coating' || projectType === 'Both';
  // Welding / line-pipe measurements — not applicable for coating-only projects
  const showWeldingPipelineFields = projectType === 'Welding' || projectType === 'Both';
  const showProductionLineSetup = showWeldingPipelineFields;
  // NDT type configured at project level (used for info banner + pre-filling new rows)
  const projectNdtType = selectedProject?.ndtType ?? '';
  // Always offer full NDT options per station regardless of project-level setting
  const stationNdtOptions = NDT_PIPELINE_TYPE_OPTIONS;

  // Debug: project type + project-level NDT when Pipeline Section modal opens
  useEffect(() => {
    if (!isModalVisible || !selectedProject) return;
    const p = selectedProject;
    const ndtRaw = p.ndtType ?? p.ndt_type;
    console.log('[Pipeline Section modal] project & NDT', {
      projectId: p.id,
      projectName: p.projectName,
      projectType: p.projectType ?? p.project_type ?? '(not set)',
      ndtTypeRaw: ndtRaw ?? '(not set)',
      projectNdtTypeResolved: projectNdtType || '(empty — banner hidden, new rows get blank NDT)',
      showCoatingFields,
      showWeldingPipelineFields,
      showProductionLineSetup,
      modalMode: editValue ? 'edit' : 'add',
      sowNdtStations:
        editValue && editingSowRecord
          ? editingSowRecord.ndtStations ?? editingSowRecord.ndt_stations ?? []
          : null,
      stationNdtOptions: stationNdtOptions.map((o) => o.value),
    });
  }, [
    isModalVisible,
    selectedProject,
    projectType,
    projectNdtType,
    showCoatingFields,
    showWeldingPipelineFields,
    showProductionLineSetup,
    editValue,
    editingSowRecord,
    stationNdtOptions,
  ]);

  const addPlRow = () =>
    setPlStationRows((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        stationNumber: '',
        // Pre-fill from project NDT type if it's a single method (RT or AUT)
        ndtType: projectNdtType && projectNdtType !== 'Both' ? projectNdtType : '',
      },
    ]);

  const removePlRow = (id) =>
    setPlStationRows((prev) => prev.filter((r) => r.id !== id));

  const updatePlRow = (id, key, value) =>
    setPlStationRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const resetProductionLine = () => {
    setPlStationRows([]);
  };

  /** New pipeline: start with one empty station row (same UX as after loading API data). */
  const setProductionLineOneBlankRow = () => {
    setPlStationRows([
      {
        id: Date.now() + Math.random(),
        stationNumber: '',
        ndtType: projectNdtType && projectNdtType !== 'Both' ? projectNdtType : '',
      },
    ]);
  };

  useEffect(() => {
    if (!isModalVisible || !editValue || !editingSowRecord) return;
    const r = editingSowRecord;
    setPipelineSec(r.pipelineSection ?? '');
    setPlannedWelds(r.plannedWelds != null && r.plannedWelds !== '' ? String(r.plannedWelds) : '');
    const al = r.actualPipelineLength ?? r.actual_pipeline_length;
    const aw = r.actualWelds ?? r.actual_welds;
    const pw = r.plannedWorks ?? r.planned_works ?? '';
    editDetailsForm.setFieldsValue({
      plannedWorks: pw === '' ? undefined : pw,
      actualPipelineLength: al != null && al !== '' ? String(al) : '',
      actualWelds: aw != null && aw !== '' ? String(aw) : '',
    });
    setMaterial(r.material ?? '');
    setStalkId(r.stalkId ?? r.stalk_id ?? '');
    setFieldJointType(r.fieldJointType ?? r.field_joint_type ?? '');
    setBlastStandard(r.blastStandard ?? r.blast_standard ?? '');
    setPipeStringId(r.pipeStringId ?? r.pipe_string_id ?? '');
    setPlStationRows(
      showProductionLineSetup ? productionLineRowsFromSow(r, projectType) : []
    );
  }, [isModalVisible, editValue, editingSowRecord, projectType, editDetailsForm, showProductionLineSetup]);

  const safePipelineData = Array.isArray(pipelineData) ? pipelineData : [];
  const filteredData = safePipelineData.filter((item) => item.pipeStringId !== uniquePipeId);
  /** Function to set values and open modal for adding pipeline details */
  const showModal = () => {
    //Able to add SOW (pipeline) on select of project
    if (selectedProject && selectedProject.id) {
      if (setEditingSowRecord) setEditingSowRecord(null);
      setPipelineSec('');
      setPlannedWelds('');
      setMaterial('');
      setStalkId('');
      setFieldJointType('');
      setBlastStandard('');
      setPipeStringId('');
      if (showProductionLineSetup) {
        setProductionLineOneBlankRow();
      } else {
        resetProductionLine();
      }
      editDetailsForm.resetFields();
      setIsModalVisible(true);
      setEditValue(false);
      setKeyValue(0);
      setChecked(true);
      setRestrict(false);
    } else {
      notification.warning({
        message: 'Please select Project to create Pipeline',
      });
    }
  };

  //
  const checkPipeStringIdUniqueness = (pipeStringId) => {
    return !safePipelineData.some((item) => item.pipeStringId === pipeStringId);
  };

  const updatedPipeStringIdUniqueness = (pipeStringId) => {
    return !filteredData.some((item) => item.pipeStringId == pipeStringId);
  };

  /** Function to set values for pipeline details */
  const handleOk = () => {
    if (submitLockRef.current || pipelineSubmitting) return;
    editDetailsForm.validateFields().then(async (data) => {
      if (!data?.pipelineSection?.trim()) {
        notification.warning({ message: 'Pipeline Section is required.' });
        return;
      }

      // Uniqueness check only when Pipe String ID is provided
      if (data.pipeStringId?.trim()) {
        if (!checkPipeStringIdUniqueness(data.pipeStringId) && !editValue) {
          notification.warning({ message: 'Pipe String ID is already in use — try another!' });
          return;
        }
        if (!updatedPipeStringIdUniqueness(data.pipeStringId) && editValue) {
          notification.warning({ message: 'Pipe String ID is already in use — try another!' });
          return;
        }
      }

      let productionLine = {};
      if (showProductionLineSetup) {
        const validRows = plStationRows.filter(
          (r) => !isNaN(Number(r.stationNumber)) && Number(r.stationNumber) > 0
        );
        const uniqueRows = Object.values(
          validRows.reduce((acc, r) => {
            const n = Number(r.stationNumber);
            if (!acc[n]) acc[n] = { stationNumber: n, ndtType: r.ndtType || projectNdtType || '' };
            return acc;
          }, {})
        ).sort((a, b) => a.stationNumber - b.stationNumber);
        const stationNums = uniqueRows.map((r) => r.stationNumber);

        if (stationNums.length > 0) {
          if (projectType === 'Welding') {
            productionLine.weldingStations = stationNums;
          } else if (projectType === 'Coating') {
            productionLine.coatingStations = stationNums;
          } else if (projectType === 'Both') {
            productionLine.weldingStations = stationNums;
            productionLine.coatingStations = stationNums;
          }
          const ndtRows = uniqueRows.filter((r) => r.ndtType);
          if (ndtRows.length > 0) {
            productionLine.ndtStations = ndtRows.map(({ stationNumber, ndtType }) => ({
              stationNumber,
              ndtType,
            }));
          }
        }
      } else if (editValue && editingSowRecord) {
        productionLine = copyProductionLineFromSowRecord(editingSowRecord, projectType);
      }

      submitLockRef.current = true;
      setPipelineSubmitting(true);
      try {
        await Promise.resolve(
          onSave(
            data.pipelineSection,
            data.material,
            data.plannedWelds,
            data.plannedWorks,
            data.stalkId,
            data.fieldJointType,
            data.blastStandard,
            data.pipeStringId,
            keyValue,
            showWeldingPipelineFields ? data.odInches : undefined,
            showWeldingPipelineFields ? data.wallThicknessMm : undefined,
            showWeldingPipelineFields ? data.expectedPipeLength : undefined,
            productionLine,
            showWeldingPipelineFields &&
            data.actualPipelineLength != null &&
            data.actualPipelineLength !== ''
              ? Number(data.actualPipelineLength)
              : undefined,
            showWeldingPipelineFields && data.actualWelds != null && data.actualWelds !== ''
              ? Number(data.actualWelds)
              : undefined
          )
        );
        setIsModalVisible(false);
        editDetailsForm.resetFields();
        resetProductionLine();
        setChecked(false);
        if (setEditingSowRecord) setEditingSowRecord(null);
      } catch (e) {
        // Parent onSave failed — keep modal open so user can retry
        console.error(e);
      } finally {
        submitLockRef.current = false;
        setPipelineSubmitting(false);
      }
    });
  };

  /** Resetting values on Closing Modal */
  const handleCancel = () => {
    editDetailsForm.resetFields();
    resetProductionLine();
    setIsModalVisible(false);
    setChecked(false);
    setEditValue(false);
    if (setEditingSowRecord) setEditingSowRecord(null);
  };

  return (
    <>
      <div
        onClick={!isWriteAccess ? showModal : undefined}
        className="new-pipeline"
      >
        <Text className="ps-3 add-new-pipeline">
          <span className="icon-22">
            <svg>
              <use xlinkHref="#add" />
            </svg>
          </span>
          Add New
        </Text>
      </div>
      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Pipeline Section"
        centered
        visible={isModalVisible}
        maskClosable={false}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleOk}
            loading={pipelineSubmitting}
            disabled={pipelineSubmitting}
          >
            {editValue ? 'Update' : 'Add'}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={editDetailsForm}
            name="basic"
            layout="vertical"
            autoComplete="off"
          >
            {/* ── Primary fields ── */}
            <Row gutter={16}>
              <Col lg={8}>
                <Form.Item
                  label="Pipeline Section"
                  name="pipelineSection"
                  required
                >
                  <Input
                    value={pipelineSec}
                    onChange={(e) => setPipelineSec(e.target.value)}
                    size="large"
                    placeholder="e.g. Bundle 3.1"
                  />
                </Form.Item>
              </Col>
              <Col lg={8}>
                {/* Same pattern as create-project Client: Form.Item name + ClientLikeSelect (no value/onChange on Select). */}
                <Form.Item label="Planned Work" name="plannedWorks">
                  <ClientLikeSelect placeholder="Select planned work type" allowClear>
                    {plannedWorkOptions?.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </ClientLikeSelect>
                </Form.Item>
              </Col>
              <Col lg={8}>
                <Form.Item label="Planned Quantity" name="plannedWelds">
                  <Input
                    value={plannedWelds}
                    onChange={(e) => setPlannedWelds(e.target.value)}
                    size="large"
                    type="number"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── Welding / line-pipe measurements (hidden for coating-only projects) ── */}
            {showWeldingPipelineFields && (
              <Row gutter={16}>
                <Col lg={8}>
                  <Form.Item label="Outer Diameter (inch)" name="odInches">
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Wall Thickness (mm)" name="wallThicknessMm">
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Expected pipe length (m)" name="expectedPipeLength">
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {showCoatingFields && (
              <Row gutter={16}>
                <Col lg={8}>
                  <Form.Item label="Material" name="material">
                    <Input
                      value={material}
                      onChange={(e) => setMaterial(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Stalk #" name="stalkId">
                    <Input
                      value={stalkId}
                      onChange={(e) => setStalkId(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Field Joint Type" name="fieldJointType">
                    <Input
                      value={fieldJointType}
                      onChange={(e) => setFieldJointType(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Blast Standard" name="blastStandard">
                    <Input
                      value={blastStandard}
                      onChange={(e) => setBlastStandard(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col lg={8}>
                  <Form.Item label="Pipe String ID" name="pipeStringId">
                    <Input
                      value={pipeStringId}
                      onChange={(e) => setPipeStringId(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {/* ── Actual results (welding projects only) ── */}
            {showWeldingPipelineFields && (
              <Row gutter={16}>
                <Col lg={12}>
                  <Form.Item label="Actual Pipeline Length (m)" name="actualPipelineLength">
                    <Input size="large" type="number" placeholder="e.g. 1250" />
                  </Form.Item>
                </Col>
                <Col lg={12}>
                  <Form.Item label="Actual Welds" name="actualWelds">
                    <Input size="large" type="number" placeholder="e.g. 84" />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form>

          {/* ── Production Line Set-Up (welding / both only — not used for coating-only projects) ── */}
          {showProductionLineSetup && (
          <div>
            <div className="pipeline-modal-step-heading">
              <div className="pipeline-modal-step-heading-line" aria-hidden />
              <span className="pipeline-modal-step-heading-text">Production Line Set-Up</span>
              <div className="pipeline-modal-step-heading-line" aria-hidden />
            </div>

            {/* Project-level NDT type info banner */}
            {projectNdtType && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: 'rgba(239,49,76,0.07)',
                  border: '1px solid rgba(239,49,76,0.25)',
                  fontSize: '12px',
                  color: '#ef314c',
                }}
              >
                <span style={{ fontWeight: 600 }}>Project NDT Type:</span>
                <span>{projectNdtType === 'Both' ? 'Both (RT & AUT) — select per station below' : projectNdtType}</span>
              </div>
            )}

            {plStationRows.length > 0 && (
              <div
                className="pipeline-modal-pl-station-header"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr 32px',
                  gap: '8px',
                  padding: '4px 2px 8px 2px',
                  marginBottom: '6px',
                }}
              >
                {['St. No.', 'NDT Type', ''].map((h, idx) => (
                  <span
                    key={h || `pl-h-${idx}`}
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#64748B',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}

            {plStationRows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr 32px',
                  gap: '8px',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <InputNumber
                  className="pl-station-row-input"
                  min={1}
                  placeholder="No."
                  value={row.stationNumber === '' ? undefined : Number(row.stationNumber)}
                  onChange={(val) => updatePlRow(row.id, 'stationNumber', val == null ? '' : String(val))}
                  style={{ width: '100%' }}
                />

                {/* NDT: merge-safe className + no showSearch (short list; search overlay breaks picks in modal rows) */}
                <ClientLikeSelect
                  showSearch={false}
                  className="white-select pl-station-row-select"
                  value={row.ndtType || undefined}
                  onChange={(val) => updatePlRow(row.id, 'ndtType', val ?? '')}
                  placeholder="Select NDT type"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {stationNdtOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </ClientLikeSelect>

                <button
                  type="button"
                  onClick={() => removePlRow(row.id)}
                  style={{
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(239,49,76,0.15)',
                    border: '1px solid rgba(239,49,76,0.35)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    color: '#ef314c',
                    fontSize: '16px',
                    lineHeight: 1,
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            <Button
              type="primary"
              size="small"
              onClick={addPlRow}
              style={{ marginTop: '16px', marginBottom: '4px', borderRadius: '20px' }}
            >
              + Add row
            </Button>
          </div>
          )}

        </div>
      </Modal>
    </>
  );
};

export default AddNewPipeline;
